import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronRight } from 'lucide-react';
import { FiX, FiShoppingBag, FiPackage } from 'react-icons/fi';
import { smartSearch } from '../services/productService';
import { generateBrandedProductSlug } from '../utils/slugUtils';

// Custom debounce function
const debounce = (func, delay) => {
    let timeoutId;
    const debouncedFunc = (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => func.apply(null, args), delay);
    };
    debouncedFunc.cancel = () => clearTimeout(timeoutId);
    return debouncedFunc;
};

const HeroSearchBar = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [searchResults, setSearchResults] = useState({ brands: [], products: [] });
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [isFocused, setIsFocused] = useState(false);
    
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    // Debounced search function
    const debouncedSearch = useCallback(
        debounce(async (searchTerm) => {
            if (!searchTerm.trim()) {
                setSearchResults({ brands: [], products: [] });
                setIsOpen(false);
                return;
            }

            try {
                setLoading(true);
                const response = await smartSearch(searchTerm, 8);
                setSearchResults(response);
                setIsOpen(true);
            } catch (error) {
                console.error('Search error:', error);
                setSearchResults({ brands: [], products: [] });
            } finally {
                setLoading(false);
            }
        }, 300),
        []
    );

    // Handle input change
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setSearchQuery(newValue);
        setSelectedIndex(-1);
        debouncedSearch(newValue);
    };

    // Handle brand selection
    const handleBrandSelect = (brand) => {
        setIsOpen(false);
        setSelectedIndex(-1);
        setSearchQuery('');
        navigate(`/products?brand=${encodeURIComponent(brand.name)}`);
        inputRef.current?.blur();
    };

    // Handle product selection
    const handleProductSelect = (product) => {
        setIsOpen(false);
        setSelectedIndex(-1);
        setSearchQuery('');
        const productSlug = generateBrandedProductSlug(product.brand, product.name);
        navigate(`/product/${productSlug}`);
        inputRef.current?.blur();
    };

    // Handle clear
    const handleClear = () => {
        setSearchQuery('');
        setSearchResults({ brands: [], products: [] });
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.focus();
    };

    // Handle search submit
    const handleSubmit = (e) => {
        e?.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
            setIsOpen(false);
            setSearchQuery('');
        }
    };

    // Get all search results as a flat array for keyboard navigation
    const getAllResults = () => {
        const results = [];
        searchResults.brands.forEach((brand, index) => {
            results.push({ ...brand, type: 'brand', index });
        });
        searchResults.products.forEach((product, index) => {
            results.push({ ...product, type: 'product', index });
        });
        return results;
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        const allResults = getAllResults();
        
        if (e.key === 'Enter') {
            e.preventDefault();
            if (selectedIndex >= 0 && allResults[selectedIndex]) {
                const selectedItem = allResults[selectedIndex];
                if (selectedItem.type === 'brand') {
                    handleBrandSelect(selectedItem);
                } else {
                    handleProductSelect(selectedItem);
                }
            } else {
                handleSubmit();
            }
            return;
        }

        if (!isOpen || allResults.length === 0) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev < allResults.length - 1 ? prev + 1 : 0
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => 
                    prev > 0 ? prev - 1 : allResults.length - 1
                );
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setSelectedIndex(-1);
                inputRef.current?.blur();
                break;
        }
    };

    // Handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
                setSelectedIndex(-1);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Clean up debounced function
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div 
            id="hero-search" 
            className="relative w-full max-w-xl mx-auto lg:mx-0" 
            ref={containerRef}
        >
            {/* Search Input Container */}
            <form onSubmit={handleSubmit}>
                <div 
                    className={`relative bg-white rounded-2xl border-2 transition-all duration-200 ${
                        isFocused 
                            ? 'border-[#ab3d22] shadow-lg shadow-[#ab3d22]/10' 
                            : 'border-gray-200 shadow-md hover:border-gray-300 hover:shadow-lg'
                    }`}
                >
                    <div className="flex items-center">
                        <div className="pl-5 sm:pl-6 flex items-center pointer-events-none">
                            <Search className={`h-5 w-5 sm:h-6 sm:w-6 transition-colors ${
                                isFocused ? 'text-[#ab3d22]' : 'text-gray-400'
                            }`} />
                        </div>
                        
                        <input
                            ref={inputRef}
                            type="text"
                            value={searchQuery}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            onFocus={() => {
                                setIsFocused(true);
                                if (searchQuery && (searchResults.brands.length > 0 || searchResults.products.length > 0)) {
                                    setIsOpen(true);
                                }
                            }}
                            onBlur={() => setIsFocused(false)}
                            placeholder="Search skincare, makeup, hair..."
                            className="flex-1 py-4 sm:py-5 px-4 text-base sm:text-lg bg-transparent border-none outline-none placeholder-gray-400 text-gray-900"
                        />
                        
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={handleClear}
                                className="p-2 mr-2 hover:bg-gray-100 rounded-full transition-colors"
                            >
                                <FiX className="h-5 w-5 text-gray-400" />
                            </button>
                        )}
                        
                        <button
                            type="submit"
                            className="m-2 px-5 sm:px-6 py-3 sm:py-3.5 bg-[#ab3d22] text-white font-semibold rounded-xl hover:bg-[#8a2f1a] transition-colors flex items-center gap-2"
                        >
                            <span className="hidden sm:inline">Search</span>
                            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                    </div>
                </div>
            </form>

            {/* Search Dropdown Results */}
            {isOpen && (
                <div className="absolute z-50 left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl max-h-80 overflow-y-auto">
                    {loading ? (
                        <div className="px-6 py-6 text-center">
                            <div className="inline-block animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-[#ab3d22]"></div>
                            <p className="text-sm text-gray-500 mt-2">Searching...</p>
                        </div>
                    ) : (searchResults.brands.length > 0 || searchResults.products.length > 0) ? (
                        <div className="py-2">
                            {/* Brands Section */}
                            {searchResults.brands.length > 0 && (
                                <div className="px-3 py-2">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2 flex items-center">
                                        <FiShoppingBag className="h-3 w-3 mr-1.5" />
                                        Brands
                                    </div>
                                    {searchResults.brands.map((brand, index) => (
                                        <button
                                            key={`brand-${brand.slug}`}
                                            type="button"
                                            onClick={() => handleBrandSelect(brand)}
                                            className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 rounded-lg transition-colors ${
                                                index === selectedIndex ? 'bg-[#ab3d22]/5' : ''
                                            }`}
                                        >
                                            <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                                <FiShoppingBag className="h-4 w-4 text-gray-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900">{brand.name}</p>
                                                <p className="text-xs text-gray-500">{brand.productCount} products</p>
                                            </div>
                                            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Divider */}
                            {searchResults.brands.length > 0 && searchResults.products.length > 0 && (
                                <div className="mx-4 my-1 border-t border-gray-100"></div>
                            )}

                            {/* Products Section */}
                            {searchResults.products.length > 0 && (
                                <div className="px-3 py-2">
                                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2 flex items-center">
                                        <FiPackage className="h-3 w-3 mr-1.5" />
                                        Products
                                    </div>
                                    {searchResults.products.map((product, index) => {
                                        const globalIndex = searchResults.brands.length + index;
                                        return (
                                            <button
                                                key={product._id}
                                                type="button"
                                                onClick={() => handleProductSelect(product)}
                                                className={`w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 rounded-lg transition-colors ${
                                                    globalIndex === selectedIndex ? 'bg-[#ab3d22]/5' : ''
                                                }`}
                                            >
                                                <img
                                                    src={product.imageUrl || '/placeholder.png'}
                                                    alt={product.name}
                                                    className="h-9 w-9 rounded-lg object-cover border border-gray-100 flex-shrink-0"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                                                    <p className="text-xs text-gray-500 truncate">by {product.brand}</p>
                                                </div>
                                                <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    ) : searchQuery.trim() && !loading ? (
                        <div className="px-6 py-6 text-center">
                            <p className="text-sm text-gray-500">No results for "{searchQuery.trim()}"</p>
                            <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
};

export default HeroSearchBar;

